import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Form, Text } from 'rsuite';
import { type CheckType } from 'schema-typed';
import { lookupAddressAPN } from '../../../../api/apiAccess';
import { useDebounce } from '../../../../hooks/useDebounce';
import { useResize } from '../../../../hooks/useResize';
import { AddressLookupAccepter, AddressOption } from '../AddressLookupAccepter';

const MINIMUM_SEARCH_TERM_LENGTH = 3;
const DEBOUNCE_DELAY = 500;
const noop = () => {};

export interface CityRestrictedAddressLookupProps {
  autoComplete?: string;
  disabled?: boolean;
  errorMessage?: string;
  hideErrorIcon?: boolean;
  label: string;
  name: string;
  onItemSelected?: (result: { address: string; apn: string[] } | null) => void;
  onTextChanged?: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  validationRule?: CheckType<unknown, any>;
  value?: string | AddressOption;
}

const CityRestrictedAddressLookup = ({
  autoComplete,
  disabled = false,
  errorMessage,
  hideErrorIcon = false,
  label,
  name,
  onItemSelected = noop,
  onTextChanged = noop,
  validationRule,
  value,
}: CityRestrictedAddressLookupProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [menuWidth, setMenuWidth] = useState('0px');
  const { resizeTrigger } = useResize();
  const [addressOptions, setAddressOptions] = useState<AddressOption[]>([]);
  const [addressAPNDataset, setAddressAPNDataset] = useState<
    Record<string, string[]>
  >({});
  const [loading, setLoading] = useState(false);
  const skipNextSearch = useRef(false);
  const realSearchTerm = useDebounce(
    typeof value === 'string' ? value : value?.value || '',
    DEBOUNCE_DELAY
  );

  useEffect(() => {
    if (containerRef.current) {
      setMenuWidth(`${containerRef.current.clientWidth}px`);
    }
  }, [resizeTrigger]);

  const hasError = useMemo(() => !!errorMessage, [errorMessage]);

  const debouncedSearch = useCallback(
    async (searchValue: string) => {
      if (skipNextSearch.current) {
        skipNextSearch.current = false;
        return;
      }
      onTextChanged();
      if (searchValue.length < MINIMUM_SEARCH_TERM_LENGTH) {
        setAddressOptions([]);
        return;
      }
      setLoading(true);
      const response = await lookupAddressAPN(searchValue);
      const data = (response?.data ?? {}) as Record<string, string[]>;
      setAddressAPNDataset(data);
      setAddressOptions(
        [...new Set<string>(Object.keys(data))].map((address) => ({
          label: address,
          value: address.replace(/\s+/g, '-'),
        }))
      );
      setLoading(false);
    },
    [onTextChanged]
  );

  useEffect(() => {
    debouncedSearch(realSearchTerm);
  }, [realSearchTerm, debouncedSearch]);

  const onAddressSelected = useCallback(
    (item?: AddressOption) => {
      skipNextSearch.current = true;
      setAddressOptions([]);
      const address = item?.label ?? item?.value ?? '';
      const matchingAPN = address ? addressAPNDataset[address] : undefined;
      onItemSelected(matchingAPN ? { address, apn: matchingAPN } : null);
    },
    [addressAPNDataset, onItemSelected]
  );

  const handleContainerBlur = useCallback(
    (e: React.FocusEvent<HTMLDivElement>) => {
      if (!containerRef.current?.contains(e.relatedTarget as Node | null)) {
        // Defer clearing so portal-rendered dropdown items can receive their click event first
        setTimeout(() => setAddressOptions([]), 200);
      }
    },
    []
  );

  return (
    <Form.Group
      controlId={name}
      data-testid="city-restricted-address-lookup"
      style={{ marginBottom: 0 }}
    >
      <Form.ControlLabel>
        <Text
          as="h5"
          className="text-bold"
        >
          {label}
        </Text>
      </Form.ControlLabel>
      <div
        ref={containerRef}
        onBlur={handleContainerBlur}
      >
        <Form.Control
          accepter={AddressLookupAccepter}
          autoComplete="street-address"
          className={disabled ? 'disabled-input' : undefined}
          data={addressOptions}
          disabled={disabled}
          errorText={errorMessage}
          hasError={hasError}
          hideErrorIcon={hideErrorIcon}
          loading={loading}
          name={name}
          onAddressSelected={onAddressSelected}
          rule={validationRule}
          style={{ maxWidth: menuWidth }}
          value={value}
        />
      </div>
    </Form.Group>
  );
};

export default CityRestrictedAddressLookup;
